import React, { SyntheticEvent, useEffect, useState } from "react";
import { Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { cloneDeep, get, merge } from "lodash";

import {
  FEEDBACK_TYPE_FEEDBACK,
  FEEDBACK_TYPE_BUGS,
  FEEDBACK_TYPE_FEATURE_REQ,
} from "../../model";
import { UserLoginInfo } from "../../types/context";
import { UserContext } from "../../context/userContext";
import { useContext } from "react";
import { appLogger, Http } from "../../utils";
import { withSnackbar } from "../../utils/common/SnackbarHoc";
import AutoAssignSettingsHeader from "./AutoAssignHeader";
import {
  enableFeedback,
  enableBugReport,
  enableFeatureRequest,
  enableChatbot,
} from "../../utils/config";
import Feedback from "./Feedback";
import Ticket from "./Ticket";
import FeatureRequest from "./FeatureRequest";
import { defaultIsAutoAssign, defaultAutoAssignSources } from "./constants";

import "./index.css";

interface AutoassignSettingsProps {
  snackbarShowMessage: any;
}

const AutoAssignSettings = ({
  snackbarShowMessage,
}: AutoassignSettingsProps) => {
  const { productId, versionId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [activeTabLabel, setActiveTabLabel] = useState("Feedback");
  const [product, setProduct] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isAutoAssign, setIsAutoAssign] = useState(defaultIsAutoAssign);
  const [autoAssignInputSource, setAutoAssignInputSource] = useState(
    defaultAutoAssignSources
  );
  const [autoAssignConfig, setAutoAssignConfig] = useState({} as any);

  const { userInfo } = useContext(UserContext) as UserLoginInfo;

  let userEnabledFeatures: any = localStorage.getItem("userenabledFeatures");
  if (userEnabledFeatures) {
    userEnabledFeatures = JSON.parse(userEnabledFeatures);
  }

  const [isFeedbackEnabled, setIsFeedbackEnabled] = useState(false);
  const [isTicketEnabled, setIsTicketEnabled] = useState(false);
  const [isFeatureRequestEnabled, setIsFeatureRequestEnabled] = useState(false);
  const [availableSource, setAvailableSource] = useState([] as any);
  const handleTabChange = (event: any, newValue: number) => {
    const tabLabel = event.target.innerText;
    setActiveTab(newValue);
    setActiveTabLabel(tabLabel);
  };
  useEffect(() => {
    setAvailableSource(() => {
      const availableList = [];
      if (isFeedbackEnabled || isTicketEnabled || isFeatureRequestEnabled) {
        availableList.push("feedback");
      }
      availableList.push("email");
      return availableList;
    });
  }, [isFeedbackEnabled, isTicketEnabled, isFeatureRequestEnabled]);

  const handleIsAutoAssignChange = (event: any, type: any) => {
    setIsAutoAssign((prevState: any) => ({
      ...prevState,
      [type]: event.target.checked,
    }));
  };

  const handleAutoAssignInputSourceChange = (
    event: any,
    type: any,
    feedbackType: any
  ) => {
    setAutoAssignConfig((prevState: any) => ({
      ...prevState,
      [feedbackType]: {
        [type]: event.target.value,
      },
    }));
  };

  const isJSONString = (value: any) => {
    try {
      JSON.parse(value);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    const updatedProduct = cloneDeep(product);
    const updatedAutoAssignConfig = {
      FEEDBACK: {
        isAutoAssign: isAutoAssign[FEEDBACK_TYPE_FEEDBACK],
        inputSource:
          autoAssignConfig[FEEDBACK_TYPE_FEEDBACK]?.inputSource || [],
      },
      BUGS: {
        isAutoAssign: isAutoAssign[FEEDBACK_TYPE_BUGS],
        inputSource: autoAssignConfig[FEEDBACK_TYPE_BUGS]?.inputSource || [],
      },
      FEATURE_REQUEST: {
        isAutoAssign: isAutoAssign[FEEDBACK_TYPE_FEATURE_REQ],
        inputSource:
          autoAssignConfig[FEEDBACK_TYPE_FEATURE_REQ]?.inputSource || [],
      },
    };
    updatedProduct.autoAssignConfig = updatedAutoAssignConfig;

    Http.put({
      url: `/api/v2/products`,
      body: {
        products: [updatedProduct],
      },
      state: userInfo,
    })
      .then(() => {
        snackbarShowMessage(
          "Auto Assign configurations saved successfully",
          "success",
          3000
        );
      })
      .catch((error: any) => {
        appLogger.error({
          errObj: {
            method: "putUpdate",
            component: "autoassign-settings",
            fileName: "index.tsx",
            err: error,
          },
        });
        snackbarShowMessage(error.message, "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const setFormData = (fetchedProduct: any): void => {
    let autoAssignProdConfig = fetchedProduct?.autoAssignConfig;
    if (!autoAssignProdConfig) {
      autoAssignProdConfig = {
        FEEDBACK: {
          isAutoAssign: false,
          inputSource: [],
        },
        BUGS: {
          isAutoAssign: false,
          inputSource: [],
        },
        FEATURE_REQ: {
          isAutoAssign: false,
          inputSource: [],
        },
      };
    }
    setIsAutoAssign(() => {
      return {
        FEEDBACK: get(autoAssignProdConfig.FEEDBACK, "isAutoAssign", false),
        BUGS: get(autoAssignProdConfig.BUGS, "isAutoAssign", false),
        FEATURE_REQ: get(
          autoAssignProdConfig.FEATURE_REQUEST,
          "isAutoAssign",
          false
        ),
      };
    });

    setAutoAssignConfig(autoAssignProdConfig);
  };

  const setTabs = (fetchedProduct: any) => {
    const feedbackTypes = fetchedProduct?.feedbackAgentSettings?.feedbackTypes;
    const feedbackEnabled =
      userEnabledFeatures?.enableFeedback &&
      enableFeedback &&
      fetchedProduct &&
      feedbackTypes &&
      feedbackTypes.includes(FEEDBACK_TYPE_FEEDBACK);
    const bugReportEnabled =
      userEnabledFeatures?.enableBugReport && enableBugReport;
    const chatbotEnabled = userEnabledFeatures?.enableChatbot && enableChatbot;
    const ticketEnabled =
      (bugReportEnabled || chatbotEnabled) &&
      fetchedProduct &&
      feedbackTypes &&
      feedbackTypes.includes(FEEDBACK_TYPE_BUGS);
    const featureRequestEnabled =
      userEnabledFeatures?.enableFeatureRequest &&
      enableFeatureRequest &&
      fetchedProduct &&
      feedbackTypes &&
      feedbackTypes.includes(FEEDBACK_TYPE_FEATURE_REQ);

    setIsFeedbackEnabled(feedbackEnabled);
    setIsTicketEnabled(ticketEnabled);
    setIsFeatureRequestEnabled(featureRequestEnabled);

    if (!feedbackEnabled && ticketEnabled) {
      setActiveTabLabel("Ticket");
    } else if (!feedbackEnabled && !ticketEnabled && featureRequestEnabled) {
      setActiveTabLabel("Feature Request");
    }
  };

  useEffect(() => {
    if (!productId || !versionId) return;

    setLoading(true);
    const fetchData = async () => {
      try {
        // Fetch product data
        const productData: any = await Http.get({
          url: `/api/v2/products/${productId}/${versionId}`,
          state: userInfo,
        });
        const fetchedProduct = productData?.products?.[0];
        if (!fetchedProduct) {
          snackbarShowMessage("Error fetching product!", "error", 3000);
          return;
        }

        setProduct(fetchedProduct);
        setFormData(fetchedProduct);
        setTabs(fetchedProduct);
      } catch (error) {
        appLogger.error({
          errObj: {
            method: "useEffect",
            component: "autoassign-settings",
            fileName: "index.tsx",
            err: error,
          },
        });

        snackbarShowMessage("Error fetching product!", "error", 3000);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [productId, versionId]);

  return (
    <div
      className={
        loading
          ? "autoassign-settings add-products-wrap loader"
          : "autoassign-settings add-products-wrap"
      }
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          padding: "0 20px",
        }}
      >
        <Box display="flex" flexDirection="column" justifyContent="end">
          <Typography
            sx={{
              borderBottom: "2px solid #0157FF",
              color: "#0157FF",
              fontFamily: "Poppins",
              fontSize: "14px",
              padding: "12px 0",
              lineHeight: "24px",
              fontWeight: 500,
            }}
          >
            Configure AutoAssign
          </Typography>
        </Box>
        <Box>
          <Button
            type="button"
            variant="outlined"
            sx={{ textTransform: "uppercase" }}
            onClick={handleCancel}
            className="create-pro-btn "
            id="pro-cancel"
          >
            close
          </Button>

          <Button
            type="submit"
            variant="contained"
            sx={{ textTransform: "uppercase" }}
            onClick={handleSave}
            className="create-pro-btn guideTourCreateSave"
            id="pro-save"
          >
            Save
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          padding: "30px",
          overflowX: "auto",
        }}
      >
        <AutoAssignSettingsHeader product={product} />

        <Tabs value={activeTab} onChange={handleTabChange} aria-label="tabs">
          {isFeedbackEnabled && <Tab label="Feedback"></Tab>}
          {isTicketEnabled && <Tab label="Ticket"></Tab>}
          {isFeatureRequestEnabled && <Tab label="Feature Request"></Tab>}
        </Tabs>

        {isFeedbackEnabled && activeTabLabel === "Feedback" && (
          <Feedback
            isAutoAssign={isAutoAssign}
            availableSource={availableSource}
            autoAssignConfig={autoAssignConfig}
            autoAssignInputSource={autoAssignInputSource}
            handleIsAutoAssignChange={handleIsAutoAssignChange}
            handleAutoAssignInputSourceChange={
              handleAutoAssignInputSourceChange
            }
          />
        )}
        {isTicketEnabled && activeTabLabel === "Ticket" && (
          <Ticket
            isAutoAssign={isAutoAssign}
            availableSource={availableSource}
            autoAssignConfig={autoAssignConfig}
            handleIsAutoAssignChange={handleIsAutoAssignChange}
            handleAutoAssignInputSourceChange={
              handleAutoAssignInputSourceChange
            }
          />
        )}
        {isFeatureRequestEnabled && activeTabLabel === "Feature Request" && (
          <FeatureRequest
            isAutoAssign={isAutoAssign}
            availableSource={availableSource}
            autoAssignConfig={autoAssignConfig}
            handleIsAutoAssignChange={handleIsAutoAssignChange}
            handleAutoAssignInputSourceChange={
              handleAutoAssignInputSourceChange
            }
          />
        )}
      </Box>
    </div>
  );
};

export default React.memo(withSnackbar(AutoAssignSettings));
